import React from "react";
import dynamic from "next/dynamic";
import { CalloutDato } from "lib/types/homepage";
import { ArticleShort } from "lib/types/article";
import { isExcludedFromLatestArticles } from "lib/cms/articleFiltering";
import MainArticle from "./MainArticle";
import FeaturedHeroArticles from "./FeaturedHeroArticles";
import SmallArticleStack from "./SmallArticleStack";

const AdManagerNoSSR = dynamic(() => import("../article/mpu"), {
  ssr: false,
});

export type HeroSectionContentProps = {
  heroArticle?: ArticleShort;
  featuredHeroArticles?: ArticleShort[];
  recentArticles?: ArticleShort[];
  callout?: CalloutDato;
  /**
   * Article category names to exclude from the "Latest Articles" list,
   * editor-managed via DatoCMS (`homepage.latestArticlesCategoriesFilter`).
   * When omitted, `isExcludedFromLatestArticles` falls back to a built-in
   * default list.
   */
  excludedLatestArticlesCategories?: string[];
};

export const HeroSectionContent = ({
  heroArticle,
  featuredHeroArticles,
  recentArticles,
  callout,
  excludedLatestArticlesCategories,
}: HeroSectionContentProps) => {
  const filteredRecentArticles = recentArticles
    ?.filter(
      (article) =>
        article.id !== heroArticle?.id &&
        !featuredHeroArticles?.some((f) => f.id === article.id) &&
        !isExcludedFromLatestArticles(article, excludedLatestArticlesCategories)
    )
    .slice(0, 4);

  return (
    <div className="mx-auto flex w-full max-w-content-mobile flex-wrap gap-4 md:max-w-content md:gap-4 lg:max-w-container mdDesktop:max-w-container">
      <div className="order-1 w-full md:order-2 md:w-[calc(50%-8px)] md:flex-none lg:w-auto lg:min-w-[316px] lg:flex-1 lg:basis-0 mdDesktop:w-auto mdDesktop:min-w-[316px] mdDesktop:flex-1 mdDesktop:basis-0">
        {heroArticle && <MainArticle article={heroArticle} />}
      </div>

      <div className="order-2 w-full md:order-1 md:w-[calc(50%-8px)] md:flex-none lg:w-auto lg:min-w-[316px] lg:flex-1 lg:basis-0 mdDesktop:w-auto mdDesktop:min-w-[316px] mdDesktop:flex-1 mdDesktop:basis-0">
        {featuredHeroArticles && featuredHeroArticles.length > 0 && (
          <FeaturedHeroArticles
            articles={featuredHeroArticles}
            callout={callout}
          />
        )}
      </div>

      <div className="order-3 flex w-full flex-col justify-between gap-4 md:flex-row lg:w-auto lg:min-w-[316px] lg:flex-1 lg:basis-0 lg:flex-col mdDesktop:w-auto mdDesktop:min-w-[316px] mdDesktop:flex-1 mdDesktop:basis-0 mdDesktop:flex-col">
        <div className="order-2 w-full md:order-1 md:w-auto md:flex-1 lg:w-full mdDesktop:w-full">
          {filteredRecentArticles && filteredRecentArticles.length > 0 && (
            <SmallArticleStack articles={filteredRecentArticles} />
          )}
        </div>

        <div className="relative order-1 mx-auto flex w-fit max-w-full shrink-0 flex-col items-center justify-end gap-0 p-2 md:order-2 md:mx-0 lg:mx-auto lg:w-full lg:max-w-[316px] mdDesktop:mx-auto mdDesktop:w-full mdDesktop:max-w-[316px]">
          <div className="flex items-center justify-center">
            <AdManagerNoSSR AdUnitID="ad_slot_0" />
          </div>
        </div>
      </div>
    </div>
  );
};

export type HeroSectionProps = HeroSectionContentProps;

export const HeroSection = (props: HeroSectionProps) => {
  return (
    <section className="w-full bg-mono-05 px-5 pt-10">
      <HeroSectionContent {...props} />
    </section>
  );
};

export default HeroSection;
